import { useState, useRef } from 'react';
import { useAuth } from '../../../auth';
import { PLATFORM_FEE_WAIVER_LABEL } from '../../../shared/platformFee';
import type { Profile, ProfilePhoto, ApplicationStatus } from '@modelcast/types';
import { Modal } from '../../../components/Modal';

interface ProfileEditModalProps {
  profile: Profile;
  onClose: () => void;
  onSaved: () => void;
}

type UploadStatus = 'idle' | 'uploading' | 'success' | 'error';

export function ProfileEditModal({ profile, onClose, onSaved }: ProfileEditModalProps) {
  const { getToken } = useAuth();
  const [isSaving, setIsSaving] = useState(false);
  const [uploadStatus, setUploadStatus] = useState<UploadStatus>('idle');
  const [photos, setPhotos] = useState<ProfilePhoto[]>(profile.photos);
  // Mandatory audit note for the save; "notify user by email" defaults OFF
  // for internal corrections (phase5 area 7 policy).
  const [auditNote, setAuditNote] = useState('');
  const [notifyUser, setNotifyUser] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [formData, setFormData] = useState({
    displayName: profile.displayName,
    bio: profile.bio ?? '',
    instagram: profile.instagram ?? '',
    website: profile.website ?? '',
    country: profile.country ?? '',
    isModel: profile.isModel,
    isArtist: profile.isArtist,
    isHost: profile.isHost,
    applicationStatus: profile.applicationStatus,
    platformFeeWaived: profile.platformFeeWaived ?? false,
  });

  async function handleSave() {
    setIsSaving(true);
    try {
      const token = await getToken();
      const response = await fetch(`/api/admin/profiles/${profile.id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          displayName: formData.displayName,
          bio: formData.bio || null,
          instagram: formData.instagram || null,
          website: formData.website || null,
          country: formData.country || null,
          isModel: formData.isModel,
          isArtist: formData.isArtist,
          isHost: formData.isHost,
          applicationStatus: formData.applicationStatus,
          platformFeeWaived: formData.platformFeeWaived,
          note: auditNote.trim(),
          notifyUser,
        }),
      });

      if (response.ok) {
        onSaved();
      } else {
        const error = await response.json();
        alert(error.error || 'Failed to save profile');
      }
    } catch (error) {
      console.error('Error saving profile:', error);
      alert('Failed to save profile');
    } finally {
      setIsSaving(false);
    }
  }

  async function handleDeletePhoto(photoId: string) {
    const note = window.prompt('Audit note for deleting this photo (required):');
    if (note === null) return;
    if (!note.trim()) {
      alert('An audit note is required to delete a photo.');
      return;
    }

    try {
      const token = await getToken();
      const response = await fetch(`/api/admin/profiles/${profile.id}/photos/${photoId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ note: note.trim() }),
      });
      if (response.ok) {
        setPhotos(photos.filter(p => p.id !== photoId));
      } else {
        const error = await response.json();
        alert(error.error || 'Failed to delete photo');
      }
    } catch (error) {
      console.error('Error deleting photo:', error);
    }
  }

  async function handlePhotoUpload(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadStatus('uploading');
    try {
      const token = await getToken();
      const formData = new FormData();
      formData.append('photo', file);

      const response = await fetch(`/api/admin/profiles/${profile.id}/photos`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: formData,
      });

      if (response.ok) {
        const data = await response.json();
        setPhotos([...photos, data.photo]);
        setUploadStatus('success');
        setTimeout(() => setUploadStatus('idle'), 2000);
      } else {
        setUploadStatus('error');
      }
    } catch (error) {
      console.error('Error uploading photo:', error);
      setUploadStatus('error');
    }
    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }

  return (
    <Modal
      onClose={onClose}
      backdropClassName="bg-black/70"
      panelClassName="bg-panel border border-line rounded-xl w-full max-w-2xl max-h-[90vh] overflow-hidden flex flex-col"
      closeOnBackdrop
      closeOnEscape={false}
      ariaLabel="Edit profile"
    >
        {/* Header */}
        <div className="px-6 py-4 border-b border-line flex items-center justify-between shrink-0">
          <h2 className="text-lg font-medium text-text">Edit Profile</h2>
          <button onClick={onClose} className="text-text-dim hover:text-text">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto p-6 space-y-5">
          {/* Photos */}
          <div>
            <div className="text-[10px] font-mono text-text-faint uppercaser mb-2">Photos</div>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handlePhotoUpload}
              className="hidden"
            />
            <div className="flex gap-2 flex-wrap items-start">
              {photos.map(photo => (
                <div key={photo.id} className="relative group">
                  <img
                    src={photo.url}
                    alt=""
                    className="w-20 h-20 rounded-lg object-cover"
                  />
                  <button
                    onClick={() => handleDeletePhoto(photo.id)}
                    className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-red-500 rounded-full flex items-center justify-center text-white opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                      <path d="M18 6L6 18M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              ))}
              {/* Add photo button */}
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploadStatus === 'uploading'}
                className="w-20 h-20 rounded-lg border border-dashed border-line hover:border-accent flex flex-col items-center justify-center text-text-faint hover:text-accent transition-colors"
              >
                {uploadStatus === 'uploading' ? (
                  <span className="text-xs">...</span>
                ) : (
                  <>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M12 5v14M5 12h14" />
                    </svg>
                    <span className="text-[10px] mt-1">Add</span>
                  </>
                )}
              </button>
            </div>
            {uploadStatus === 'success' && (
              <span className="text-xs text-ok mt-1 block">Photo added!</span>
            )}
            {uploadStatus === 'error' && (
              <span className="text-xs text-warn mt-1 block">Upload failed</span>
            )}
          </div>

          {/* Name */}
          <div>
            <label className="block text-[10px] font-mono text-text-faint uppercaser mb-2">
              Display Name
            </label>
            <input
              type="text"
              value={formData.displayName}
              onChange={(e) => setFormData(f => ({ ...f, displayName: e.target.value }))}
              className="input w-full"
            />
          </div>

          {/* Bio */}
          <div>
            <label className="block text-[10px] font-mono text-text-faint uppercaser mb-2">
              Bio
            </label>
            <textarea
              value={formData.bio}
              onChange={(e) => setFormData(f => ({ ...f, bio: e.target.value }))}
              className="input w-full h-24 resize-none"
            />
          </div>

          {/* Social */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-[10px] font-mono text-text-faint uppercaser mb-2">
                Instagram
              </label>
              <input
                type="text"
                value={formData.instagram}
                onChange={(e) => setFormData(f => ({ ...f, instagram: e.target.value }))}
                placeholder="username"
                className="input w-full"
              />
            </div>
            <div>
              <label className="block text-[10px] font-mono text-text-faint uppercaser mb-2">
                Website
              </label>
              <input
                type="url"
                value={formData.website}
                onChange={(e) => setFormData(f => ({ ...f, website: e.target.value }))}
                placeholder="https://"
                className="input w-full"
              />
            </div>
          </div>

          {/* Country */}
          <div>
            <label className="block text-[10px] font-mono text-text-faint uppercaser mb-2">
              Country Code
            </label>
            <input
              type="text"
              value={formData.country}
              onChange={(e) => setFormData(f => ({ ...f, country: e.target.value.toUpperCase().slice(0, 2) }))}
              placeholder="US"
              maxLength={2}
              className="input w-24"
            />
          </div>

          {/* Roles */}
          <div>
            <label className="block text-[10px] font-mono text-text-faint uppercaser mb-2">
              Roles
            </label>
            <div className="flex gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.isModel}
                  onChange={(e) => setFormData(f => ({ ...f, isModel: e.target.checked }))}
                  className="w-4 h-4 rounded"
                />
                <span className="text-sm text-text">Model</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.isArtist}
                  onChange={(e) => setFormData(f => ({ ...f, isArtist: e.target.checked }))}
                  className="w-4 h-4 rounded"
                />
                <span className="text-sm text-text">Artist</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.isHost}
                  onChange={(e) => setFormData(f => ({ ...f, isHost: e.target.checked }))}
                  className="w-4 h-4 rounded"
                />
                <span className="text-sm text-text">Host</span>
              </label>
            </div>
          </div>

          {/* Platform Fee Waiver */}
          <div>
            <label className="block text-[10px] font-mono text-text-faint uppercaser mb-2">
              Platform Fee
            </label>
            <label className="flex items-start gap-3 cursor-pointer rounded-lg border border-line bg-panel2/50 p-3">
              <input
                type="checkbox"
                checked={formData.platformFeeWaived}
                onChange={(e) => setFormData(f => ({ ...f, platformFeeWaived: e.target.checked }))}
                className="mt-0.5 w-4 h-4 rounded"
              />
              <span className="text-sm text-text">
                {PLATFORM_FEE_WAIVER_LABEL}
              </span>
            </label>
          </div>

          {/* Application Status */}
          <div>
            <label className="block text-[10px] font-mono text-text-faint uppercaser mb-2">
              Application Status
            </label>
            <select
              value={formData.applicationStatus ?? ''}
              onChange={(e) => setFormData(f => ({
                ...f,
                applicationStatus: (e.target.value || null) as ApplicationStatus | null,
              }))}
              className="input w-full"
            >
              <option value="">None</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
            </select>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-line shrink-0 bg-panel space-y-3">
          <div>
            <label className="block text-[10px] font-mono text-text-faint uppercaser mb-2">
              Audit note (required)
            </label>
            <textarea
              value={auditNote}
              onChange={(e) => setAuditNote(e.target.value)}
              placeholder="Why is this profile being edited? Recorded in the audit log."
              className="input w-full h-16 resize-none"
            />
          </div>
          <div className="flex items-center justify-between gap-3">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={notifyUser}
                onChange={(e) => setNotifyUser(e.target.checked)}
                className="w-4 h-4 rounded"
              />
              <span className="text-sm text-text">Notify user by email</span>
            </label>
            <div className="flex gap-3">
              <button onClick={onClose} className="btn" disabled={isSaving}>
                Cancel
              </button>
              <button
                onClick={handleSave}
                disabled={isSaving || !formData.displayName.trim() || !auditNote.trim()}
                className="btn btn-accent"
              >
                {isSaving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
    </Modal>
  );
}
